import { AlertTriangle, Clock } from 'lucide-react';
import { StandardModal } from './StandardModal';
import { Button, OutlineButton } from './Button';

interface SessionTimeoutWarningProps {
  isOpen: boolean;
  secondsRemaining: number;
  onStayLoggedIn: () => void;
  onLogout: () => void;
}

/**
 * Modal warning users that their session is about to expire.
 *
 * Shows a countdown timer and allows users to extend their session
 * or logout immediately.
 */
export default function SessionTimeoutWarning({
  isOpen,
  secondsRemaining,
  onStayLoggedIn,
  onLogout,
}: SessionTimeoutWarningProps) {
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <StandardModal
      open={isOpen}
      onClose={onStayLoggedIn}
      size="sm"
      closeOnBackdrop={false}
      closeOnEscape={false}
      hideHeader
    >
      <div className="flex flex-col items-center gap-4 py-4">
        {/* Warning Icon */}
        <div className="h-16 w-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
          <AlertTriangle className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">
          Session Expiring Soon
        </h2>

        {/* Message */}
        <p className="text-center text-zinc-600 dark:text-zinc-400">
          Your session will expire due to inactivity. Would you like to stay logged in?
        </p>

        {/* Countdown */}
        <div className="flex items-center gap-3 px-6 py-4 bg-zinc-100 dark:bg-zinc-800 rounded-lg">
          <Clock className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
          <div className="flex flex-col">
            <span className="text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">
              Time Remaining
            </span>
            <span className="text-2xl font-mono font-semibold text-zinc-900 dark:text-white tabular-nums">
              {formatTime(secondsRemaining)}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 w-full pt-2">
          <OutlineButton onClick={onLogout} className="flex-1" size="md">
            Logout
          </OutlineButton>
          <Button onClick={onStayLoggedIn} variant="primary" className="flex-1" size="md">
            Stay Logged In
          </Button>
        </div>
      </div>
    </StandardModal>
  );
}
